import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faDownload,
  faMagnifyingGlass,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../../services/Interceptor";
import { getCandidateAthletes } from "../../../services/AdminService";
import CandidateAthletesMobileList from "./CandidateAthletesMobileList";

const PAGE_SIZE = 25;

const normalize = (str) =>
  (str ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

const COLUMNS = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "eventName", label: "Event Name" },
  { key: "highSchool", label: "HighSchool" },
  { key: "expectedYearOfGraduation", label: "Expected Year Of Graduation" },
  { key: "athletePhoneNumber", label: "Athlete PhoneNumber" },
  { key: "parentPhoneNumber", label: "Parent PhoneNumber" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "createdAtUTC", label: "Date" },
];

export default function AdminCandidateAthletes() {
  const apiInstance = useAxiosInstance();
  const [athletes, setAthletes] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pastMonth: 0,
    pastWeek: 0,
    past24Hours: 0,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const fetchData = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await getCandidateAthletes(apiInstance, page, PAGE_SIZE);
      setAthletes(data.results ?? []);
      setStats(
        data.stats ?? { total: 0, pastMonth: 0, pastWeek: 0, past24Hours: 0 },
      );
      setTotalPages(data.totalPages ?? 1);
      setTotalResults(data.totalResults ?? 0);
    } catch (err) {
      setError(
        typeof err === "string" ? err : (err?.message ?? "Failed to load data"),
      );
    } finally {
      setLoading(false);
    }
  }, [apiInstance, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const filteredAthletes = (() => {
    const tokens = normalize(searchQuery).trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return athletes;
    return athletes.filter((a) => {
      const haystack = `${normalize(a.firstName)} ${normalize(a.lastName)} ${normalize(a.email)} ${normalize(a.highSchool)}`;
      return tokens.every((t) => haystack.includes(t));
    });
  })();

  const sortedAthletes = (() => {
    if (!sortCol) return filteredAthletes;
    return [...filteredAthletes].sort((a, b) => {
      const av = a[sortCol] ?? "";
      const bv = b[sortCol] ?? "";
      const cmp = String(av).localeCompare(String(bv), undefined, {
        numeric: true,
      });
      return sortDir === "asc" ? cmp : -cmp;
    });
  })();

  const startRecord = totalResults === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(page * PAGE_SIZE, totalResults);

  const handleExport = () => {
    const headers = COLUMNS.map((c) => c.label);
    const rows = sortedAthletes.map((a) =>
      COLUMNS.map((c) => {
        const val =
          c.key === "createdAtUTC" ? formatDate(a[c.key]) : (a[c.key] ?? "");
        return `"${String(val).replace(/"/g, '""')}"`;
      }),
    );
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "candidate-athletes.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderSortIcon = (col) => {
    if (sortCol !== col)
      return (
        <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-400 text-xs" />
      );
    return (
      <FontAwesomeIcon
        icon={sortDir === "asc" ? faSortUp : faSortDown}
        className="ml-1 text-red-600 text-xs"
      />
    );
  };

  const renderPagination = () => {
    if (totalResults === 0) return null;

    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm flex-wrap gap-2">
        <span className="text-gray-600">
          Showing {startRecord}&ndash;{endRecord} of {totalResults} results
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
            className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          {pages.map((p, i) =>
            p === "..." ? (
              <span key={`ellipsis-${i}`} className="px-2 text-gray-500">
                &hellip;
              </span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`px-3 py-1 rounded border ${
                  p === page
                    ? "bg-red-500 text-white border-red-500"
                    : "border-gray-300 hover:bg-gray-100"
                }`}
              >
                {p}
              </button>
            ),
          )}
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages}
            className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="overflow-y-auto overflow-x-hidden h-full p-4 bg-white lg:bg-gray-100">
      {/* Desktop table — lg and above */}
      <div className="hidden lg:block min-w-0">
        <div className="bg-white shadow-lg rounded-lg p-4">
          {/* Title */}
          <h1 className="text-xl mb-5 uppercase text-gray-800">
            POTENTIAL ATHLETE SIGNUPS
          </h1>

          {/* Stats + Export + Search — single row */}
          <div className="flex items-center justify-between mb-4">
            {/* Stats (flat, no cards) */}
            <div className="flex items-center gap-8">
              {[
                { label: "TOTAL", value: stats.total },
                { label: "THIS WEEK", value: stats.pastWeek },
                { label: "NEW TODAY", value: stats.past24Hours },
                { label: "PAST MONTH", value: stats.pastMonth },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-xs text-gray-600 uppercase">{s.label}</p>
                  <p className="text-2xl font-bold text-gray-800">{s.value}</p>
                </div>
              ))}
            </div>

            {/* Export + Search */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleExport}
                className="flex items-center gap-2 border border-gray-400 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-100"
              >
                <FontAwesomeIcon icon={faDownload} />
                Export CSV
              </button>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
                />
                <input
                  type="text"
                  placeholder="Name/Email/School"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-8 pr-3 py-2 border border-gray-300 rounded text-sm w-52 focus:outline-none focus:ring-1 focus:ring-red-400"
                />
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center py-10 text-gray-500">Loading...</div>
          )}
          {error && !loading && (
            <div className="text-center py-10 text-red-500">{error}</div>
          )}

          {!loading && !error && (
            <div className="bg-white rounded-lg shadow">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-red-100 border-b border-red-200">
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          className="px-3 py-3 text-left font-semibold text-gray-700 cursor-pointer select-none text-sm"
                        >
                          <div className="flex flex-col">
                            <span>{col.label}</span>
                            <span>{renderSortIcon(col.key)}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAthletes.length === 0 ? (
                      <tr>
                        <td
                          colSpan={COLUMNS.length}
                          className="text-center py-10 text-gray-500"
                        >
                          No results found
                        </td>
                      </tr>
                    ) : (
                      sortedAthletes.map((a) => (
                        <tr
                          key={a.id}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-3 py-1 text-gray-800">
                            {a.firstName}
                          </td>
                          <td className="px-3 py-1 text-gray-800">
                            {a.lastName}
                          </td>
                          <td className="px-3 py-1 text-gray-800">{a.email}</td>
                          <td className="px-3 py-1 text-gray-800">
                            {a.eventName}
                          </td>
                          <td className="px-3 py-1 text-gray-800">
                            {a.highSchool}
                          </td>
                          <td className="px-3 py-1 text-gray-800 text-center">
                            {a.expectedYearOfGraduation}
                          </td>
                          <td className="px-3 py-1 text-gray-800">
                            {a.athletePhoneNumber}
                          </td>
                          <td className="px-3 py-1 text-gray-800">
                            {a.parentPhoneNumber}
                          </td>
                          <td className="px-3 py-1 text-gray-800">{a.city}</td>
                          <td className="px-3 py-1 text-gray-800">{a.state}</td>
                          <td className="px-3 py-1 text-gray-800">
                            {formatDate(a.createdAtUTC)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </div>
          )}
        </div>
      </div>

      {/* Mobile list — below lg */}
      <div className="lg:hidden">
        <CandidateAthletesMobileList
          athletes={sortedAthletes}
          stats={stats}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onExport={handleExport}
          loading={loading}
          error={error}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          startRecord={startRecord}
          endRecord={endRecord}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
